import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, FolderIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { projectsAPI } from '../services/api';
import { notify } from '../hooks/useNotificationStore';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

interface Project {
  id: number;
  name: string;
  description: string;
  chat_count: number;
  document_count?: number;
  created_at: string;
}

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.list();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      notify.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await projectsAPI.create({ name: newName, description: newDescription });
      const newProject = response.data;
      setNewName('');
      setNewDescription('');
      setShowCreate(false);
      notify.success('Project created', `"${newName}" has been created.`);
      navigate(`/projects/${newProject.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      notify.error('Failed to create project');
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;

    try {
      await projectsAPI.delete(projectToDelete.id);
      loadProjects();
      notify.success('Project deleted', `"${projectToDelete.name}" has been deleted.`);
    } catch (error) {
      console.error('Failed to delete project:', error);
      notify.error('Failed to delete project');
    } finally {
      setProjectToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-surface-100">Projects</h1>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            New Project
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="card mb-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Create Project
            </h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className="input-field"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary">
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Project list */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <p className="text-surface-400">No projects yet</p>
            <button
              onClick={() => setShowCreate(true)}
              className="btn-primary mt-4"
            >
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className="group card flex items-center hover:bg-surface-700/50 transition-colors"
              >
                <Link
                  to={`/projects/${project.id}`}
                  className="flex items-center flex-1 min-w-0"
                >
                  <FolderIcon className="w-8 h-8 text-primary-400 flex-shrink-0 mr-4" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-surface-100">
                      {project.name}
                    </h3>
                    {project.description && (
                      <p className="text-surface-400 text-sm mt-1 truncate">
                        {project.description}
                      </p>
                    )}
                    <p className="text-surface-500 text-xs mt-2">
                      {project.chat_count} {project.chat_count === 1 ? 'chat' : 'chats'}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-surface-500 group-hover:text-surface-300 transition-colors flex-shrink-0 ml-4" />
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setProjectToDelete(project);
                  }}
                  className="p-2 ml-2 rounded opacity-0 group-hover:opacity-100 text-surface-400 hover:text-red-400 hover:bg-surface-600 transition-all"
                  title="Delete project"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={projectToDelete !== null}
        onClose={() => setProjectToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Project"
        message={`Are you sure you want to delete "${projectToDelete?.name}"? This will permanently delete all chats and documents in this project.`}
      />
    </div>
  );
}
